import { z } from 'zod';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { WriteBackToFountainStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/fountain-write-back';
import { WriteBackToLeverStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/lever-write-back';
import { WriteBackToGreenhouseStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/greenhouse-write-back';
import { WriteBackToWorkdayViaWsStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/workday-write-back';
import { WriteBackToAdpStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/adp-write-back';
import { WriteBackToAshbyStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/ashby-write-back';
import { WriteBackOverrideRule } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { WorkflowExplanationFormat } from 'libs/end-to-end/src/types/databases/workflows/workflow-explanation-format';
import { WorkflowSimplifiedExplanationConfig } from 'libs/end-to-end/src/types/databases/workflows/workflow-explanation-format';

export const WorkflowHiringReadinessExplanationWriteBackStrategy = z.union([
  WriteBackToGreenhouseStrategy,
  WriteBackToFountainStrategy,
  WriteBackToLeverStrategy,
  WriteBackToWorkdayViaWsStrategy,
  WriteBackToAdpStrategy,
  WriteBackToAshbyStrategy,
]);

export type WorkflowHiringReadinessExplanationWriteBackStrategy = z.infer<
  typeof WorkflowHiringReadinessExplanationWriteBackStrategy
>;

/**
 * One named group in V2 explanation write-back configuration (Fountain-only strategies today).
 */
export const WorkflowHiringReadinessExplanationWriteBackV2ConfigOption =
  z.object({
    groupName: z.string(),
    strategy: WriteBackToFountainStrategy.optional(),
    mode: WorkflowWriteBackMode,

    /**
     * Optional format for generated explanation text when using numeric overrides / templates.
     */
    format: WorkflowExplanationFormat.optional(),

    /**
     * When operating in the simplified explanations mode, this provides additional configuration.
     */
    simplifiedExplanationConfig: WorkflowSimplifiedExplanationConfig.optional(),

    /**
     * When true, omits the readiness score from the produced explanation text
     */
    excludeScoreFromText: z.boolean().optional(),

    /**
     * When true, caps the number of traits included in the produced explanation
     * text at a hardcoded limit (currently 10). Defaults to false (all traits).
     */
    limitExplanationFactors: z.boolean().optional(),

    /**
     * Rules evaluated against `readinessRating` on the revision candidate index row.
     * First matching rule wins; string override values become the full note body for Fountain.
     */
    overrideRules: z.array(WriteBackOverrideRule),
  });

export type WorkflowHiringReadinessExplanationWriteBackV2ConfigOption = z.infer<
  typeof WorkflowHiringReadinessExplanationWriteBackV2ConfigOption
>;

export const WorkflowHiringReadinessExplanationWriteBackV2Config = z.object({
  options: z.array(WorkflowHiringReadinessExplanationWriteBackV2ConfigOption),

  /**
   * Optional fallback configuration for applicants without a group or with an empty group.
   * If not provided, applicants without groups will be skipped.
   */
  noGroupConfig: WorkflowHiringReadinessExplanationWriteBackV2ConfigOption.omit(
    {
      groupName: true,
    },
  ).optional(),
});

export type WorkflowHiringReadinessExplanationWriteBackV2Config = z.infer<
  typeof WorkflowHiringReadinessExplanationWriteBackV2Config
>;

/**
 * Details for a workflow that writes back hiring readiness explanation.
 */
export const WorkflowHiringReadinessExplanationWriteBackDetails = z.object({
  type: z.literal('hiringReadinessExplanationWriteBack'),

  /**
   * The strategy for writing back.
   */
  strategy: WorkflowHiringReadinessExplanationWriteBackStrategy.optional(),

  /**
   * The mode for writing back.
   */
  mode: WorkflowWriteBackMode.optional(),

  /**
   * V2 Write Back Config
   */
  v2Config: WorkflowHiringReadinessExplanationWriteBackV2Config.optional(),

  /**
   * The number of previous days of applicants to consider for writing back.
   */
  numPreviousDaysOfApplicants: z.number().optional(),

  /**
   * The minimum readiness rating for an applicant to be considered for writing back.
   */
  minReadinessRating: z.number().optional(),

  /**
   * Number of samples to send
   */
  numberOfSamplesToSend: z.number().optional(),

  /**
   * When true, also skip write back when a prior successful payload hash exists
   * for this entity on this schedule (duplicate payload is always skipped).
   */
  skipWriteBackIfDuplicate: z.boolean().optional(),

  /**
   * The format for the explanation text.
   */
  format: WorkflowExplanationFormat.optional(),

  /**
   * When operating in the simplified explanations mode, this provides additional configuration.
   */
  simplifiedExplanationConfig: WorkflowSimplifiedExplanationConfig.optional(),

  /**
   * When true, caps the number of traits included in the produced explanation
   * text at a hardcoded limit (currently 10). Defaults to false (all traits).
   * Currently only honored by the Fountain strategy.
   */
  limitExplanationFactors: z.boolean().optional(),
});

export type WorkflowHiringReadinessExplanationWriteBackDetails = z.infer<
  typeof WorkflowHiringReadinessExplanationWriteBackDetails
>;

export const determineHiringReadinessExplanationApplicantConfig = ({
  groupName,
  v2Config,
}: {
  groupName: string | undefined;
  v2Config: WorkflowHiringReadinessExplanationWriteBackV2Config | undefined;
}):
  | {
      type: 'success';
      strategy: WriteBackToFountainStrategy;
      mode: WorkflowWriteBackMode;
      overrideRules: WriteBackOverrideRule[];
      format: WorkflowExplanationFormat;
      simplifiedExplanationConfig:
        | WorkflowSimplifiedExplanationConfig
        | undefined;
      excludeScoreFromText: boolean;
      limitExplanationFactors: boolean;
      groupName: string;
      isNoGroupFallback: boolean;
    }
  | { type: 'skip'; reason: string }
  | { type: 'error'; reason: string } => {
  if (!v2Config) {
    return {
      type: 'skip',
      reason: 'V2 configuration is required',
    };
  }

  const trimmedGroup = groupName?.trim() || '';

  if (!trimmedGroup) {
    if (v2Config.noGroupConfig && v2Config.noGroupConfig.strategy) {
      return {
        type: 'success',
        strategy: v2Config.noGroupConfig.strategy,
        mode: v2Config.noGroupConfig.mode,
        overrideRules: v2Config.noGroupConfig.overrideRules ?? [],
        format: v2Config.noGroupConfig.format ?? 'long',
        simplifiedExplanationConfig:
          v2Config.noGroupConfig.simplifiedExplanationConfig,
        excludeScoreFromText:
          v2Config.noGroupConfig.excludeScoreFromText ?? false,
        limitExplanationFactors:
          v2Config.noGroupConfig.limitExplanationFactors ?? false,
        groupName: '(No Group - Using Fallback)',
        isNoGroupFallback: true,
      };
    }
    return {
      type: 'skip',
      reason: 'No group assigned and no fallback configuration',
    };
  }

  const matchingGroup = v2Config.options.find(
    (opt) => opt.groupName === trimmedGroup,
  );

  if (!matchingGroup) {
    return {
      type: 'error',
      reason: `Group "${trimmedGroup}" not found in configuration`,
    };
  }

  if (!matchingGroup.strategy) {
    return {
      type: 'skip',
      reason: `Group "${trimmedGroup}" has no strategy configured`,
    };
  }

  return {
    type: 'success',
    strategy: matchingGroup.strategy,
    mode: matchingGroup.mode,
    overrideRules: matchingGroup.overrideRules ?? [],
    format: matchingGroup.format ?? 'long',
    simplifiedExplanationConfig: matchingGroup.simplifiedExplanationConfig,
    excludeScoreFromText: matchingGroup.excludeScoreFromText ?? false,
    limitExplanationFactors: matchingGroup.limitExplanationFactors ?? false,
    groupName: matchingGroup.groupName,
    isNoGroupFallback: false,
  };
};
